import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { windowSummary } from '../utils/ledgerWindow';
import { glassText } from '../utils/glassSurface';

/**
 * THE CUSTOM WINDOW: the ledger's figures for a stretch the reader names, in the glass panel.
 *
 * <h2>It computes NOTHING of its own</h2>
 *
 * {@link windowSummary} filters and then calls `annualBreakdown`, whose total row is the same
 * arithmetic the annual table above it uses and is pinned against the backend's own statistics.
 * A component with its own accumulator is how two panels a centimetre apart come to report
 * different numbers for the same trades — which this repository has already had once.
 *
 * <h2>Why it is worth having at all</h2>
 *
 * Every band headline in this codebase has turned out to be carried by an era or a handful of
 * trades: PTA's filtered cohort was +40.54% whole, -10.88% without its best three, and PF 0.51
 * through the 2010s. The annual rows make that visible a year at a time; a window makes it
 * answerable for the stretch a reader is actually looking at — a drawdown, a regime, one quarter.
 *
 * <h2>It is bounded by the PLAYHEAD, because its input is</h2>
 *
 * `trades` is whatever the caller passes, and the caller passes `tradesClosedBy(timeline, index)`.
 * So a window whose end date is ahead of the playhead reports only the part that has happened —
 * which is the whole point of the replay and not something this component should be able to
 * override by holding a date.
 */

const HEAD = {
  color: glassText(0.8),
  fontSize: 10,
  letterSpacing: 0.6,
  fontWeight: 700,
  lineHeight: 1.6,
};

const CELL = {
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.7,
  whiteSpace: 'nowrap',
};

export const GAIN = '#10B981';
export const LOSS = '#EF4444';

const pct = (v, places = 1) => `${v >= 0 ? '+' : ''}${v.toFixed(places)}%`;

/** "n/a", never a large number: an absent denominator is not a perfect score. */
const pf = (v) => (v === null || v === undefined ? 'n/a' : v.toFixed(2));

/**
 * A date input small enough for the dock, styled to the glass rather than to the MUI default.
 *
 * <p>A native `type="date"` rather than a picker component: the dock is 389px wide and every
 * picker in this application opens a popover the size of the panel it sits in. The native control
 * is also what the setup dialog uses, so the two read as the same kind of field.
 */
const DateField = ({ value, onChange, testId, label }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: '1 1 0', minWidth: 0 }}>
    <Typography sx={{ ...HEAD, fontSize: 9, opacity: 0.75 }}>{label}</Typography>
    <Box
      component="input"
      type="date"
      data-testid={testId}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        font: 'inherit',
        fontSize: 11,
        color: glassText(0.95),
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 1,
        padding: '2px 4px',
        width: '100%',
        minWidth: 0,
        // The native picker indicator is black on a dark surface, which is invisible rather than
        // merely ugly -- a reader cannot find the control they are meant to click.
        colorScheme: 'dark',
      }}
    />
  </Box>
);

DateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

export default function ReplayWindowSummary({
  trades, from, to, onChange, open: initiallyOpen, onFocus,
}) {
  const [open, setOpen] = React.useState(!!initiallyOpen);

  // Computed on every render: a filter and one pass over at most a few hundred trades, in a HUD
  // that already redraws per bar. Memoising it would add a dependency on an array identity that
  // changes every tick anyway.
  const summary = windowSummary(trades, { from, to });
  // WHETHER A WINDOW IS SET AT ALL. With neither bound the window is every trade, which the
  // cumulative figures above already report -- so the header says "all trades" rather than a
  // count that would look like a filter nobody applied.
  const bounded = !!from || !!to;

  /**
   * A date change applies the window AND takes the chart to it.
   *
   * <h2>Immediately, on the change, rather than behind an Apply button</h2>
   *
   * The brief asks for the chart to pan and scale "immediately", and a native date input fires
   * its change only on a COMPLETE date -- there is no half-typed state to debounce, so the
   * keystroke-by-keystroke problem a text field would have does not arise here.
   *
   * <p>A focus is dispatched only when the window names at least one bound. With neither, the
   * window is every trade and the chart is already showing whatever the reader left it on;
   * yanking it to the whole series because somebody cleared a date would undo a view they had
   * chosen by another route.
   */
  const set = (patch) => {
    const next = { from, to, ...patch };
    onChange(next);
    if (onFocus && (next.from || next.to)) onFocus(next);
  };

  return (
    <Box data-testid="window-summary" sx={{ mt: 1.25 }}>
      <Box sx={{ height: 1, background: 'rgba(255,255,255,0.10)', mb: 1 }} />
      <Box
        component="button"
        type="button"
        data-testid="window-summary-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{
          ...HEAD,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          cursor: 'pointer',
          background: 'transparent',
          border: 0,
          padding: 0,
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <Box component="span" sx={{ fontSize: 9, opacity: 0.7 }}>{open ? '▾' : '▸'}</Box>
        CUSTOM WINDOW
        <Box component="span" sx={{ ml: 'auto', opacity: 0.6, fontWeight: 400 }}>
          {/* The count is on the HEADER, so a reader knows whether the window holds anything
              before opening it -- and so a collapsed panel still says a date range is set. */}
          {bounded
            ? (summary ? `${summary.trades} trades` : 'none in window')
            : 'all trades'}
        </Box>
      </Box>

      {open ? (
        <Box sx={{ mt: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
            <DateField label="FROM" value={from} testId="window-from" onChange={(v) => set({ from: v })} />
            <DateField label="TO" value={to} testId="window-to" onChange={(v) => set({ to: v })} />
            <Box
              component="button"
              type="button"
              data-testid="window-clear"
              // CLEAR does not move the chart, deliberately -- see the note on `set`. It is the
              // one gesture here whose meaning is "stop filtering", not "look somewhere else".
              onClick={() => onChange({ from: null, to: null })}
              sx={{
                ...HEAD,
                fontSize: 9,
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 1,
                color: glassText(0.8),
                padding: '3px 6px',
                fontFamily: 'inherit',
                flex: '0 0 auto',
              }}
            >
              CLEAR
            </Box>
          </Box>

          {summary ? (
            <Box sx={{ mt: 0.75 }}>
              {[
                ['TRADES', String(summary.trades)],
                ['WIN RATE', `${summary.winRatePercent.toFixed(1)}%`],
                ['PROFIT FACTOR', pf(summary.profitFactor)],
                ['MAX DRAWDOWN', `${summary.maxDrawdownPercent.toFixed(2)}%`],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ ...CELL, color: glassText(0.7) }}>{label}</Typography>
                  <Typography sx={{ ...CELL, color: glassText(0.95) }}>{value}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ ...CELL, color: glassText(0.7) }}>GROSS RETURN</Typography>
                <Typography
                  data-testid="window-return"
                  sx={{
                    ...CELL,
                    fontWeight: 600,
                    color: summary.netPnlPercent > 0
                      ? GAIN
                      : summary.netPnlPercent < 0 ? LOSS : glassText(0.95),
                  }}
                >
                  {pct(summary.netPnlPercent, 2)}
                </Typography>
              </Box>
              {/* THE DOLLARS ONLY WHEN THE RUN WAS SIZED. Null is not zero: a currency row of
                  0.00 on an unsized run reads as a book that made nothing. */}
              {summary.netPnl === null ? null : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ ...CELL, color: glassText(0.7) }}>NET PnL</Typography>
                  <Typography
                    sx={{
                      ...CELL,
                      fontWeight: 600,
                      color: summary.netPnl > 0 ? GAIN : summary.netPnl < 0 ? LOSS : glassText(0.95),
                    }}
                  >
                    {summary.netPnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography data-testid="window-empty" sx={{ ...CELL, mt: 0.75, color: glassText(0.7) }}>
              {/* NOT a row of zeros. A window with nothing closed in it has no win rate and no
                  profit factor, and 0% / 0.00 would read as a measurement of a flat period. */}
              No trades closed in this window yet.
            </Typography>
          )}

          <Typography sx={{ ...HEAD, fontSize: 9, fontWeight: 400, opacity: 0.65, mt: 0.5 }}>
            Trades are counted by EXIT date, inclusive of both days, and the curve restarts inside
            the window — so the drawdown is measured from the window’s own peak. Bounded by the
            playhead: a window reaching past it shows only what has happened.
            {onFocus ? ' Setting a date also takes the chart to that window.' : ''}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

ReplayWindowSummary.propTypes = {
  /** The run's completed trades, already bounded by the playhead. Stamped or bare. */
  trades: PropTypes.array,
  /** `yyyy-mm-dd`, or null for unbounded on that side. */
  from: PropTypes.string,
  to: PropTypes.string,
  /** Called with `{from, to}` — the window is the caller's state, not this component's. */
  onChange: PropTypes.func.isRequired,
  /** Start expanded. Off by default, like the annual table: the dock is a column over a chart. */
  open: PropTypes.bool,
  /** Called with `{from, to}` when a bound changes. Absent leaves the filter chart-independent. */
  onFocus: PropTypes.func,
};
